import { create } from 'zustand';
import { profileImageCache } from '@/lib/services/profile-image-cache.service';
import { useAuth } from '@/store/auth.store';
import { createLogger } from '@/lib/logger';

const logger = createLogger('store/user-profile.store');

// UI State only - profile image URLs cache for quick access
interface UserProfileState {
  // Separate current user image from other profiles for easier access
  currentUserImageUrl: string | null;
profileImageUrls: Record<string, string | null>;
  
  // Helper methods (profile images are fetched via TanStack Query)
  getProfileImageUrl: (userId: string) => string | null;
  
  // Preload images (delegates to cache service)
  preloadProfileImages: (userIds: string[]) => Promise<void>;
  
  // Reset on organization change
  resetOrganizationData: () => void;
}

/**
 * Simplified UserProfileStore that only manages UI state
 * Server state (fetching, updating) is managed by TanStack Query
 */
export const useUserProfileStore = create<UserProfileState>()((set, get) => ({
  currentUserImageUrl: null,
  profileImageUrls: {},
  
  // Helper to get cached image URL (images are fetched via TanStack Query)
  getProfileImageUrl: (userId: string) => {
    const state = get();
    const { user } = useAuth.getState();
    
    // Return current user image if requesting current user
    if (user && user.id === userId) {
      return state.currentUserImageUrl;
    }
    
    return state.profileImageUrls[userId] || null;
  },

  // Preload images (delegates to centralized cache)
  preloadProfileImages: async (userIds: string[]) => {
    if (userIds.length === 0) return;
    
    try {
      // Delegate to centralized cache for batching
      await profileImageCache.preloadImages(userIds);
      
      // Update state with any cached results
      const images = await profileImageCache.getProfileImages(userIds);
      set(state => ({
        profileImageUrls: {
          ...state.profileImageUrls,
          ...images
        }
      }));
    } catch (error) {
      logger.error('[UserProfileStore] Error preloading images:', error);
    }
  },
  
  resetOrganizationData: () => {
    // Only clear other users' profile images, preserve current user's image
    const { user } = useAuth.getState();
    const currentUserId = user?.id;
    
    // Clear other users' profile images from cache
    const currentState = get();
    const otherUserIds = Object.keys(currentState.profileImageUrls);
    
    if (otherUserIds.length > 0) {
      profileImageCache.invalidateUsers(otherUserIds);
    }
    
    // Clear other users' profile URLs from state but preserve current user data
    set({
      profileImageUrls: {},
    });
  },
}));
